import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Clock, Target, Brain, CheckCircle, AlertCircle, Activity, Zap, Shield, Eye, Camera, ChevronDown, ChevronUp, Terminal, Sparkles, ArrowUpDown, List, ListFilter } from 'lucide-react';
import useWebSocket from '../hooks/useWebSocket';
import { getScanWebSocketUrl } from '../utils/websocket';
import ScreenshotGallery from './ScreenshotGallery';
import MarkdownRenderer from './common/MarkdownRenderer';

const ScanTimeline = ({ scanId }) => {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedEvents, setExpandedEvents] = useState({});
  const [liveAIText, setLiveAIText] = useState({});
  const [viewMode, setViewMode] = useState('last5'); // 'last5', 'all'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc' (desc = newest first)
  const textRefs = useRef({});
  const processedEventIds = useRef(new Set()); // Track processed events to avoid duplicates
  const { lastMessage, isConnected } = useWebSocket(getScanWebSocketUrl(scanId));

  // Fetch timeline from API
  const fetchTimeline = async () => {
    try {
      // Get auth token from localStorage (try both keys for compatibility)
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      // Use relative URL for proxy in development, or REACT_APP_API_URL in production
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/v1/scan/${scanId}/timeline`, { headers });
      if (response.ok) {
        const data = await response.json();
        if (data && data.length > 0) {
          // Merge with existing timeline to preserve live updates
          setTimeline(prevTimeline => {
            const existingIds = new Set(prevTimeline.map(e => e.id));
            const newEvents = data.filter(e => !existingIds.has(e.id));
            // Update existing events with fresh data
            const updated = prevTimeline.map(existing => {
              const fresh = data.find(d => d.id === existing.id);
              return fresh ? { ...existing, ...fresh } : existing;
            });
            return [...updated, ...newEvents];
          });
          // Initialize live AI text from existing data
          data.forEach(event => {
            if (event.ai_output || event.thinking || event.stdout) {
              setLiveAIText(prev => ({
                ...prev,
                [event.id]: event.ai_output || event.thinking || event.stdout || ''
              }));
            }
            // Track processed events
            processedEventIds.current.add(event.id);
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and smart polling (reduced when WebSocket connected)
  useEffect(() => {
    fetchTimeline(); // Initial fetch always

    // Smart polling: fast when WebSocket disconnected, slow when connected
    const pollingInterval = isConnected ? 10000 : 5000; // 10s when WS up, 5s when down
    const interval = setInterval(fetchTimeline, pollingInterval);

    return () => clearInterval(interval);
  }, [scanId, isConnected]);

  // Handle WebSocket events for real-time updates
  useEffect(() => {
    if (lastMessage && lastMessage.scan_id === scanId) {
      if (lastMessage.type === 'scan_event') {
        const event = lastMessage.event;

        // Check if we've already processed this exact event version
        const eventKey = `${event.id}-${event.timestamp}`;
        if (processedEventIds.current.has(eventKey)) {
          return; // Skip duplicate
        }
        processedEventIds.current.add(eventKey);
        processedEventIds.current.add(event.id);

        setTimeline(prevTimeline => {
          const existingIndex = prevTimeline.findIndex(e => e.id === event.id);

          if (existingIndex >= 0) {
            // Update existing event
            const updated = [...prevTimeline];
            updated[existingIndex] = { ...updated[existingIndex], ...event };
            return updated;
          } else {
            // Add new event
            return [...prevTimeline, event];
          }
        });

        // Update live AI text
        if (event.ai_output || event.thinking || event.stdout) {
          setLiveAIText(prev => ({
            ...prev,
            [event.id]: event.ai_output || event.thinking || event.stdout || ''
          }));
        }
      }

      // Handle AI streaming text (append mode - no duplicate check needed)
      if (lastMessage.type === 'ai_stream' && lastMessage.event_id) {
        setLiveAIText(prev => ({
          ...prev,
          [lastMessage.event_id]: (prev[lastMessage.event_id] || '') + lastMessage.text
        }));
      }
    }
  }, [lastMessage, scanId]);

  // Auto-scroll for live text
  useEffect(() => {
    Object.keys(expandedEvents).forEach(eventId => {
      if (expandedEvents[eventId] && textRefs.current[eventId]) {
        const element = textRefs.current[eventId];
        element.scrollTop = element.scrollHeight;
      }
    });
  }, [liveAIText, expandedEvents]);

  const toggleExpand = (eventId) => {
    setExpandedEvents(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };

  // Map string types to icons
  const getIconForType = (type, id) => {
    if (id === 'screenshots') return Camera;
    switch(type) {
      case 'action': return Target;
      case 'result': return CheckCircle;
      case 'ai_thinking': return Brain;
      default: return Activity;
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'action': return 'border-blue-500/50 bg-blue-500/10';
      case 'result': return 'border-green-500/50 bg-green-500/10';
      case 'ai_thinking': return 'border-purple-500/50 bg-purple-500/10';
      default: return 'border-gray-500/50 bg-gray-500/10';
    }
  };

  const getIconColor = () => {
    return 'text-white';  // All icons in white
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || '';
    if (s === 'completed' || s === 'success' || s === 'done') return 'text-green-500';
    if (s === 'processing' || s === 'running' || s === 'in_progress') return 'text-yellow-500 animate-pulse';
    if (s === 'critical' || s === 'error') return 'text-red-500';
    if (s === 'failed') return 'text-red-500';
    return 'text-blue-400'; // Default to blue for info/neutral states
  };

  // Translate status to user-friendly French text
  const getStatusLabel = (event) => {
    const status = event.status?.toLowerCase() || '';

    // If result type with data, it's likely analysis complete
    if (event.type === 'result' || event.type === 'ai_thinking') {
      if (status === 'completed' || status === 'success' || status === 'done') return 'Analysis complete';
      if (status === 'processing' || status === 'running') return 'Analyse en cours...';
    }

    // Standard mappings
    if (status === 'completed' || status === 'success' || status === 'done') return 'Done';
    if (status === 'processing' || status === 'running' || status === 'in_progress') return 'En cours...';
    if (status === 'critical') return 'Critique';
    if (status === 'failed' || status === 'error') return 'Erreur';

    // If status is empty or unknown but has exit_code
    if (event.exit_code === 0 || event.exit_code === '0') return 'Done';
    if (event.exit_code && event.exit_code !== '' && event.exit_code !== 0) return 'Erreur';

    // For AI orchestrator results without clear status
    if (event.tool === 'ai_orchestrator' || event.title?.toLowerCase().includes('ai')) {
      return event.ai_output || event.thinking ? 'AI analysis complete' : 'Waiting...';
    }

    return status || 'En attente...';
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDuration = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diff = Math.floor((now - then) / 1000);

    if (diff < 60) return `il y a ${diff}s`;
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)}min`;
    return `il y a ${Math.floor(diff / 3600)}h`;
  };

  // Check if event has expandable content
  const hasExpandableContent = (event) => {
    return event.ai_explanation || event.thinking || event.ai_output || event.stdout ||
           liveAIText[event.id] || event.type === 'ai_thinking' || event.type === 'result';
  };

  // Filter and sort timeline based on viewMode and sortOrder
  const filteredTimeline = useMemo(() => {
    let result = [...timeline];

    // Sort by timestamp
    result.sort((a, b) => {
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    // Apply view mode filter
    if (viewMode === 'last5') {
      result = result.slice(0, 5);
    }

    return result;
  }, [timeline, viewMode, sortOrder]);

  if (!loading && timeline.length === 0) {
    return null;
  }

  return (
    <div className="bg-brand-black-light rounded-xl p-6 border border-brand-green/30">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-6 h-6 text-brand-green" />
        <h2 className="text-xl font-bold text-brand-green">Timeline du Scan</h2>
        {isConnected && (
          <span className="flex items-center gap-1 text-xs text-green-400">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            En direct
          </span>
        )}

        {/* Filter and Sort Buttons */}
        <div className="ml-auto flex items-center gap-2">
          {/* View Mode Buttons */}
          <div className="flex rounded-lg overflow-hidden border border-gray-700">
            <button
              onClick={() => setViewMode('last5')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5 ${
                viewMode === 'last5'
                  ? 'bg-brand-green text-black'
                  : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
              title="Afficher les 5 derniers"
            >
              <ListFilter className="w-3.5 h-3.5" />
              5 derniers
            </button>
            <button
              onClick={() => setViewMode('all')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5 border-l border-gray-700 ${
                viewMode === 'all'
                  ? 'bg-brand-green text-black'
                  : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
              title="Afficher tout"
            >
              <List className="w-3.5 h-3.5" />
              Tout ({timeline.length})
            </button>
          </div>

          {/* Sort Button */}
          <button
            onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors flex items-center gap-1.5 ${
              sortOrder === 'desc'
                ? 'border-brand-green/50 bg-brand-green/10 text-brand-green'
                : 'border-blue-500/50 bg-blue-500/10 text-blue-400'
            }`}
            title={sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
          >
            <ArrowUpDown className="w-3.5 h-3.5" />
            {sortOrder === 'desc' ? 'Recent' : 'Ancien'}
          </button>
        </div>
      </div>

      {/* Info bar */}
      {timeline.length > 5 && viewMode === 'last5' && (
        <div className="mb-4 px-3 py-2 bg-gray-800/50 rounded-lg border border-gray-700/50 text-xs text-gray-400 flex items-center justify-between">
          <span>Affichage des 5 derniers evenements sur {timeline.length} au total</span>
          <button
            onClick={() => setViewMode('all')}
            className="text-brand-green hover:underline"
          >
            Voir tout
          </button>
        </div>
      )}

      <div className="relative">
        {/* Ligne verticale */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-brand-green/20"></div>

        <div className="space-y-6">
          {filteredTimeline.map((event) => {
            const EventIcon = getIconForType(event.type, event.id);
            const isExpanded = expandedEvents[event.id];
            const hasContent = hasExpandableContent(event);
            // Console shows raw tool stdout preferentially; ai_output for live streaming.
            // Never shows event.thinking alone since that's already in the purple box.
            const consoleText = event.stdout || liveAIText[event.id] || event.ai_output || '';

            return (
              <div key={event.id} className="relative pl-16">
                {/* Icon */}
                <div className={`absolute left-0 top-0 w-12 h-12 rounded-full border-2 grid place-items-center ${getTypeColor(event.type)}`}>
                  <EventIcon className="w-6 h-6 text-white" />
                </div>

                {/* Contenu */}
                <div
                  className={`bg-brand-black rounded-lg border border-gray-800 hover:border-brand-green/50 transition-all ${hasContent ? 'cursor-pointer' : ''}`}
                  onClick={() => hasContent && toggleExpand(event.id)}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-bold text-white">{typeof event.title === 'string' ? event.title : String(event.title || '')}</h3>
                          {event.type === 'ai_thinking' && (
                            <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded flex items-center gap-1">
                              <Brain className="w-3 h-3" />
                              IA
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-400">{typeof event.description === 'string' ? event.description : String(event.description || '')}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-xs text-gray-500">{formatTime(event.timestamp)}</div>
                          <div className="text-xs text-gray-600">{formatDuration(event.timestamp)}</div>
                        </div>
                        {hasContent && (
                          <button className="p-1 hover:bg-gray-800 rounded transition-colors">
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Data indicators */}
                    {event.data && (
                      <div className="flex gap-4 text-xs mb-2">
                        {Object.entries(event.data).map(([key, value]) => (
                          <div key={key} className="flex items-center gap-1">
                            <span className="text-gray-500 capitalize">{key}:</span>
                            <span className="text-brand-green font-bold">{value}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Status indicator */}
                    <div className="flex items-center gap-3">

                      {/* Progress bar */}
                      {event.status === 'processing' && event.progress !== undefined && (
                        <div className="flex-1 max-w-xs flex items-center gap-2">
                          <div className="w-full bg-gray-800 rounded-full h-1.5">
                            <div
                              className="bg-brand-green h-full rounded-full transition-all duration-500"
                              style={{ width: `${event.progress}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-500">{event.progress}%</span>
                        </div>
                      )}

                      {hasContent && !isExpanded && (event.ai_explanation || consoleText) && (
                        <span className="text-xs text-purple-400 flex items-center gap-1">
                          <Terminal className="w-3 h-3" />
                          Click to view details
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Expanded AI Text Area */}
                  {isExpanded && (
                    <div className="border-t border-gray-800">
                      {/* Dynamic AI Explanation Header */}
                      {(event.ai_explanation || event.type === 'ai_thinking' || event.thinking || event.tool === 'ai_orchestrator') && (
                        <div className="p-3 bg-purple-500/5 border-b border-purple-500/20">
                          <div className="flex items-center gap-2 text-sm text-purple-400 mb-2">
                            <Sparkles className="w-4 h-4" />
                            <span className="font-medium">
                              {event.type === 'ai_thinking' ? 'Analyse IA' : 'Explication dynamique'}
                            </span>
                            {event.status === 'processing' && (
                              <span className="ml-auto flex items-center gap-1 text-xs">
                                <span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-pulse"></span>
                                Analyse en cours...
                              </span>
                            )}
                          </div>
                          {/* Dynamic AI explanation based on actual results */}
                          <div className="text-sm text-gray-300 space-y-2">
                            {event.ai_explanation ? (
                              <MarkdownRenderer content={event.ai_explanation} variant="compact" />
                            ) : (
                              <div className="space-y-1">
                                <p className="flex items-center gap-2">
                                  <Brain className="w-3 h-3 text-purple-400" />
                                  {event.status === 'processing'
                                    ? "L'IA analyse la surface d'attaque et planifie les prochaines actions."
                                    : "Analysis complete."}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Raw output console - only shown when stdout/stream is available */}
                      {consoleText && (
                        <div
                          ref={el => textRefs.current[event.id] = el}
                          className="p-4 max-h-96 overflow-y-auto bg-gray-900/50 font-mono text-sm"
                        >
                          <div className="whitespace-pre-wrap text-gray-300 leading-relaxed">
                            {consoleText.split('\n').map((line, i) => {
                              let className = 'text-gray-300';
                              if (line.startsWith('[') && line.includes(']')) {
                                className = 'text-blue-400';
                              } else if (line.toLowerCase().includes('vulnerability') || line.toLowerCase().includes('vulne')) {
                                className = 'text-red-400';
                              } else if (line.toLowerCase().includes('success') || line.toLowerCase().includes('found')) {
                                className = 'text-green-400';
                              } else if (line.toLowerCase().includes('analyzing') || line.toLowerCase().includes('analyse')) {
                                className = 'text-yellow-400';
                              } else if (line.startsWith('#') || line.startsWith('##')) {
                                className = 'text-purple-400 font-bold';
                              } else if (line.startsWith('-') || line.startsWith('*')) {
                                className = 'text-cyan-400';
                              }
                              return (
                                <div key={i} className={className}>
                                  {line || '\u00A0'}
                                </div>
                              );
                            })}
                            {event.status === 'processing' && (
                              <span className="inline-block w-2 h-4 bg-brand-green animate-pulse ml-1"></span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Screenshots Gallery if present */}
                      {event.screenshots && event.screenshots.length > 0 && (
                        <div className="p-4 border-t border-gray-800">
                          <ScreenshotGallery screenshots={event.screenshots} scanId={scanId} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Indicateur scan en cours */}
        {filteredTimeline.length > 0 && filteredTimeline.some(e => e.status === 'processing') && (
          <div className="relative pl-16 mt-6">
            <div className="absolute left-0 w-12 h-12 rounded-full border-2 border-brand-green/30 bg-brand-green/10 flex items-center justify-center">
              <Activity className="w-6 h-6 text-brand-green animate-pulse" />
            </div>
            <div className="bg-brand-black rounded-lg p-4 border border-brand-green/30">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-brand-green rounded-full animate-pulse"></div>
                <p className="text-sm text-gray-400">Scan en cours...</p>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ScanTimeline;
